import React, { useEffect, useState } from 'react';
import { getAuditPredictions, getAuditPredictionDetail, generateExplanation } from '../services/api';
import '../styles/PredictionAuditTrail.css';

const PredictionAuditTrail = () => {
  const [filters, setFilters] = useState({
    modelName: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [hasSearched, setHasSearched] = useState(false);

  const [selectedRow, setSelectedRow] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerData, setDrawerData] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [generatingExplanation, setGeneratingExplanation] = useState({});

  const token = localStorage.getItem('adminToken');

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = async (pageNum = 1) => {
    if (!token) return;

    setLoading(true);
    setHasSearched(true);
    try {
      const params = {
        model_name: filters.modelName || undefined,
        start_date: filters.startDate,
        end_date: filters.endDate,
        page: pageNum,
        page_size: pageSize
      };

      const response = await getAuditPredictions(token, params);
      const data = response.data;

      setResults(data.items || []);
      setTotalRecords(data.total || 0);
      setCurrentPage(data.page || 1);
    } catch (error) {
      console.error('Error fetching audit predictions:', error);
      alert('Error: ' + (error.response?.data?.detail || error.message));
      setResults([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    handleSearch(newPage);
  };

  const handleRowClick = async (row) => {
    setSelectedRow(row);
    setDrawerOpen(true);
    setDrawerLoading(true);

    try {
      const response = await getAuditPredictionDetail(token, row.uid);
      const data = response.data;
      setDrawerData(data);
    } catch (error) {
      console.error('Error fetching audit detail:', error);
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleGenerateExplanation = async () => {
    if (!selectedRow) return;

    const uid = selectedRow.uid;
    setGeneratingExplanation(prev => ({ ...prev, [uid]: true }));

    try {
      const response = await generateExplanation(token, uid);
      const data = response.data;

      if (data.url) {
        window.open(data.url, '_blank');

        setResults(prev => prev.map(item =>
          item.uid === uid ? { ...item, has_explanation: true } : item
        ));

        if (drawerData) {
          setDrawerData(prev => ({
            ...prev,
            explanation_url: data.url
          }));
        }
      }
    } catch (error) {
      console.error('Error generating explanation:', error);
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGeneratingExplanation(prev => ({ ...prev, [uid]: false }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const truncateUid = (uid, maxLength = 20) => {
    return uid && uid.length > maxLength ? uid.substring(0, maxLength) + '...' : uid;
  };

  const renderDumpDataTable = (data) => {
    if (!data || typeof data !== 'object') return <p>No data available</p>;

    return (
      <table className="pat-drawer-table">
        <tbody>
          {Object.entries(data).map(([key, value]) => (
            <tr key={key}>
              <td className="pat-drawer-key">{key}</td>
              <td className="pat-drawer-value">
                {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const totalPages = Math.ceil(totalRecords / pageSize);

  return (
    <div className="pat-wrapper">
      <div className="pat-header">
        <h2>Prediction Audit Trail</h2>
        <p>View all real-time predictions made via the routing endpoint</p>
      </div>

      <div className="pat-filter-section">
        <div className="pat-search-form">
          <input
            type="text"
            placeholder="Model Name (optional)"
            value={filters.modelName}
            onChange={(e) => handleFilterChange('modelName', e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch(1)}
          />
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
          />
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
          />
          <button
            className="pat-search-btn"
            onClick={() => handleSearch(1)}
            disabled={loading}
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </div>

      {hasSearched && (
        <div className="pat-results-section">
          {loading ? (
            <div className="pat-loading">Loading predictions...</div>
          ) : results.length === 0 ? (
            <div className="pat-no-results">No predictions found</div>
          ) : (
            <>
              <div className="pat-table-wrapper">
                <table className="pat-table">
                  <thead>
                    <tr>
                      <th>UID</th>
                      <th>Model</th>
                      <th>Version</th>
                      <th>Timestamp</th>
                      <th>Explanation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((row) => (
                      <tr key={row.uid} onClick={() => handleRowClick(row)} className="pat-table-row">
                        <td>
                          <span className="pat-uid" title={row.uid}>
                            {truncateUid(row.uid)}
                          </span>
                        </td>
                        <td>{row.model_name}</td>
                        <td>{row.version}</td>
                        <td>{formatDate(row.created_at)}</td>
                        <td>
                          {row.has_explanation ? (
                            <a
                              href="#"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (drawerData?.explanation_url) {
                                  window.open(drawerData.explanation_url, '_blank');
                                }
                              }}
                              className="pat-explanation-link"
                            >
                              View
                            </a>
                          ) : (
                            <button
                              className="pat-generate-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedRow(row);
                                handleGenerateExplanation();
                              }}
                              disabled={generatingExplanation[row.uid]}
                            >
                              {generatingExplanation[row.uid] ? 'Generating...' : 'Generate'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="pat-pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <span className="pat-page-info">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {drawerOpen && (
        <div className="pat-drawer-overlay" onClick={() => setDrawerOpen(false)}>
          <div className="pat-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="pat-drawer-header">
              <h3>Prediction Details</h3>
              <button className="pat-drawer-close" onClick={() => setDrawerOpen(false)}>
                ×
              </button>
            </div>

            {drawerLoading ? (
              <div className="pat-drawer-loading">Loading details...</div>
            ) : drawerData ? (
              <div className="pat-drawer-content">
                <div className="pat-drawer-section">
                  <h4>Basic Information</h4>
                  <div className="pat-drawer-info">
                    <div className="pat-info-row">
                      <span className="pat-info-label">UID:</span>
                      <span className="pat-info-value">{drawerData.uid}</span>
                    </div>
                    <div className="pat-info-row">
                      <span className="pat-info-label">Model:</span>
                      <span className="pat-info-value">{drawerData.model_name}</span>
                    </div>
                    <div className="pat-info-row">
                      <span className="pat-info-label">Version:</span>
                      <span className="pat-info-value">{drawerData.version}</span>
                    </div>
                    <div className="pat-info-row">
                      <span className="pat-info-label">Endpoint:</span>
                      <span className="pat-info-value">{drawerData.endpoint}</span>
                    </div>
                    <div className="pat-info-row">
                      <span className="pat-info-label">Timestamp:</span>
                      <span className="pat-info-value">{formatDate(drawerData.created_at)}</span>
                    </div>
                  </div>
                </div>

                {drawerData.dump_data.InputData && (
                  <div className="pat-drawer-section">
                    <h4>Input Data (Raw)</h4>
                    <div className="pat-drawer-table-wrapper">
                      {renderDumpDataTable(drawerData.dump_data.InputData)}
                    </div>
                  </div>
                )}

                {drawerData.dump_data.PreModeling && (
                  <div className="pat-drawer-section">
                    <h4>Pre-Modeling Data</h4>
                    <div className="pat-drawer-table-wrapper">
                      {Array.isArray(drawerData.dump_data.PreModeling)
                        ? renderDumpDataTable(drawerData.dump_data.PreModeling[0] || {})
                        : renderDumpDataTable(drawerData.dump_data.PreModeling)}
                    </div>
                  </div>
                )}

                {drawerData.dump_data.ModelOutput && (
                  <div className="pat-drawer-section">
                    <h4>Model Output</h4>
                    <div className="pat-drawer-table-wrapper">
                      {Array.isArray(drawerData.dump_data.ModelOutput)
                        ? renderDumpDataTable(drawerData.dump_data.ModelOutput[0] || {})
                        : renderDumpDataTable(drawerData.dump_data.ModelOutput)}
                    </div>
                  </div>
                )}

                {drawerData.dump_data.ApiResponse && (
                  <div className="pat-drawer-section">
                    <h4>API Response (Post)</h4>
                    <div className="pat-drawer-table-wrapper">
                      {Array.isArray(drawerData.dump_data.ApiResponse)
                        ? renderDumpDataTable(drawerData.dump_data.ApiResponse[0] || {})
                        : renderDumpDataTable(drawerData.dump_data.ApiResponse)}
                    </div>
                  </div>
                )}

                <div className="pat-drawer-section">
                  <h4>Explanation</h4>
                  {drawerData.explanation_url ? (
                    <button
                      className="pat-view-explanation-btn"
                      onClick={() => window.open(drawerData.explanation_url, '_blank')}
                    >
                      View Explanation
                    </button>
                  ) : (
                    <button
                      className="pat-generate-explanation-btn"
                      onClick={handleGenerateExplanation}
                      disabled={generatingExplanation[drawerData.uid]}
                    >
                      {generatingExplanation[drawerData.uid] ? 'Generating...' : 'Generate Explanation'}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="pat-drawer-error">Error loading details</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PredictionAuditTrail;
